// Photoroom cutouts carry stray alpha 1-2 pixels at the image edges; treat those as transparent.
const AUTO_CROP_ALPHA_THRESHOLD = 16;

export default class CanvasFunctions {
  static hexToRgb(hex) {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (m, r, g, b) {
      return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  static simpleRemoveBackground(canvas, colorHex, amount) {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let hasNonTransparentPixels = false;

    // if background actually needs to be removed
    if (amount > 0) {
      const data = imageData.data;
      const color = CanvasFunctions.hexToRgb(colorHex);

      // define lower and upper color boundaries
      const min = {
        r: color.r - amount,
        g: color.g - amount,
        b: color.b - amount,
      };
      const max = {
        r: color.r + amount,
        g: color.g + amount,
        b: color.b + amount,
      };

      // use constants for readability
      const r = 0;
      const g = 1;
      const b = 2;
      const a = 3;

      for (let p = 0; p < data.length; p += 4) {
        // make pixels transparent if it falls into defined color range
        if (
          data[p + r] <= max.r &&
          data[p + g] <= max.g &&
          data[p + b] <= max.b &&
          data[p + r] >= min.r &&
          data[p + g] >= min.g &&
          data[p + b] >= min.b
        ) {
          data[p + a] = 0;
        } else {
          hasNonTransparentPixels = true;
        }
      }
    }

    // only remove background if there are still some nontransparent pixels left
    if (hasNonTransparentPixels) {
      ctx.putImageData(imageData, 0, 0);
    }
  }

  static consoleLogCanvas(canvas) {
    console.log(
      '%c       ',
      `font-size: ${canvas.height}px; background: url(${canvas.toDataURL()}) no-repeat;`
    );
  }

  static convertImageDataToCanvasImageSource(imageData) {
    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = imageData.width;
    tmpCanvas.height = imageData.height;
    const tmpCtx = tmpCanvas.getContext('2d', { willReadFrequently: true });
    tmpCtx.putImageData(imageData, 0, 0);

    return tmpCanvas;
  }

  static manualRemoveBackground(canvas, cuttingAreas) {
    const tmpCanvas = document.createElement('canvas');
    const tmpCtx = tmpCanvas.getContext('2d');

    const coordinatesCorrection = {
      x: 0,
      y: 0,
    };

    // update old cut-out areas
    if (Array.isArray(cuttingAreas[0])) {
      const updatedCuttingAreas = [];
      cuttingAreas.forEach((coordinates) => {
        updatedCuttingAreas.push({
          x: coordinates[0],
          y: coordinates[1],
        });
      });
      cuttingAreas = [
        {
          coordinates: updatedCuttingAreas,
          status: 'closed',
        },
      ];
    }

    tmpCanvas.width = canvas.width;
    tmpCanvas.height = canvas.height;

    // clip outline
    cuttingAreas[0].coordinates.forEach((coordinates) => {
      if (coordinates.type === 'curve') {
        tmpCtx.quadraticCurveTo(
          coordinates.cpx - coordinatesCorrection.x,
          coordinates.cpy - coordinatesCorrection.y,
          coordinates.x - coordinatesCorrection.x,
          coordinates.y - coordinatesCorrection.y
        );
      } else {
        tmpCtx.lineTo(
          coordinates.x - coordinatesCorrection.x,
          coordinates.y - coordinatesCorrection.y
        );
      }
    });
    tmpCtx.clip();

    // paste the source into the clipped canvas
    const ctx = canvas.getContext('2d');

    tmpCtx.drawImage(canvas, 0, 0);

    // cut out inside of the image
    tmpCtx.globalCompositeOperation = 'destination-out';
    cuttingAreas.forEach((cuttingArea, index) => {
      if (index > 0) {
        tmpCtx.beginPath();
        cuttingArea.coordinates.forEach((coordinates) => {
          if (coordinates.type === 'curve') {
            tmpCtx.quadraticCurveTo(
              coordinates.cpx - coordinatesCorrection.x,
              coordinates.cpy - coordinatesCorrection.y,
              coordinates.x - coordinatesCorrection.x,
              coordinates.y - coordinatesCorrection.y
            );
          } else {
            tmpCtx.lineTo(
              coordinates.x - coordinatesCorrection.x,
              coordinates.y - coordinatesCorrection.y
            );
          }
        });
        tmpCtx.fill();
      }
    });

    // clear original canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // draw the result to original canvas
    ctx.drawImage(tmpCanvas, 0, 0);
  }

  static feather(canvas, distance = 1) {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const width = canvas.width;

    // Safari 9.1 workaround (it doesn't support Uint8Array Slicing, so we apply no feather in this case)
    if (!Uint8Array.prototype.slice) {
      return;
    }

    const data = imageData.data;
    // perform checks on the copy of original data
    const origData = data.slice();

    const clearAmount = Math.floor(255 / (distance + 1));

    // iterate through all pixels
    for (let i = 0; i < data.length; i += 4) {
      // how many pixels do we want to feathers
      // we settle for 1 as a default since this function is quite slow
      for (let j = 1; j <= distance; j++) {
        const widthModulo = (i / 4) % width;
        let clearStrength = 0;

        // check pixel(s) to the left (and account for the left edge)
        if (widthModulo === 0 || origData[i - 4 * j + 3] === 0) {
          clearStrength++;
        }

        // check pixel(s) to the right (and account for the right edge)
        if (widthModulo === width - 1 || origData[i + 4 * j + 3] === 0) {
          clearStrength++;
        }

        // check pixel(s) to the top (and account for the beginning of the image)
        const ta = origData[i - width * j * 4 + 3];
        if (!ta) {
          clearStrength++;
        }

        // check pixel(s) to the bottom (and account for the bottom of the image)
        const ba = origData[i + width * j * 4 + 3];
        if (!ba) {
          clearStrength++;
        }

        data[i + 3] -= clearAmount * clearStrength;
      }
    }

    // overwrite original image
    ctx.putImageData(imageData, 0, 0);
  }

  static getAutoCropData(canvas) {
    const ctx = canvas.getContext('2d');
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);

    let top = null;
    let left = null;
    let right = null;
    let bottom = null;

    for (let i = 0; i < pixels.data.length; i += 4) {
      // gather non-transparent pixels
      if (pixels.data[i + 3] > AUTO_CROP_ALPHA_THRESHOLD) {
        const x = (i / 4) % canvas.width;
        const y = ~~(i / 4 / canvas.width);

        if (top === null) {
          top = y;
        }

        if (left === null) {
          left = x;
        } else if (x < left) {
          left = x;
        }

        if (right === null) {
          right = x;
        } else if (right < x) {
          right = x;
        }

        if (bottom === null) {
          bottom = y;
        } else if (bottom < y) {
          bottom = y;
        }
      }
    }

    // fully transparent image: keep a 1x1 box so the item still has a size
    if (top === null) {
      return { left: 0, top: 0, width: 1, height: 1 };
    }

    // arrays have 0-based numbering so we add 1 to compensate for that
    // as image can't be "0px" wide/tall
    bottom = bottom + 1;
    right = right + 1;

    const height = bottom - top;
    const width = right - left;

    return { left, top, width, height };
  }

  static crop(canvas, cropData) {
    const ctx = canvas.getContext('2d');
    const trimmed = ctx.getImageData(
      cropData.cropLeft === undefined ? 500 : cropData.cropLeft,
      cropData.cropTop === undefined ? 500 : cropData.cropTop,
      cropData.width,
      cropData.height
    );

    canvas.width = cropData.width;
    canvas.height = cropData.height;
    ctx.putImageData(trimmed, 0, 0);
  }
}
